import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, EVENTS, STATUS, StoreHelpers } from 'react-joyride';
import { OnboardingTours } from './onboarding.enums';
import tourMap from './tours';
import { useUserContext } from '../contexts/user';
import { useLocalStorage } from '../hooks';
import { onboardingStepToStep } from './onboarding.converters';

export interface OnboardingOptions {
    disableAutoOpen?: boolean;
}

enum Status {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
}

export const useOnboardingTour = (
    tour: OnboardingTours,
    options: OnboardingOptions = {}
) => {
    const { user } = useUserContext();
    const [run, setRun] = useState(true);
    const [index, setIndex] = useState(0);
    const [status, setStatus] = useLocalStorage(
        `TRAVELITY_${user?.id}_${tour}`,
        Status.NOT_STARTED
    );

    const helpersRef = useRef<StoreHelpers | null>(null);

    useEffect(() => {
        const currentStep = tourMap[tour][index];
        if (currentStep.nextOnTargetClick && currentStep.className) {
            const clickHandler = (event: MouseEvent) => {
                const target = event.target as HTMLElement;
                if (target.closest(`.${currentStep.className}`)) {
                    helpersRef.current?.next();
                }
            };

            document.addEventListener('click', clickHandler);
            return () => {
                document.removeEventListener('click', clickHandler);
            };
        }

        return () => {};
    }, [index, tour]);

    const steps = useMemo(() => {
        const allSteps = tourMap[tour].map(onboardingStepToStep);
        allSteps[0].disableBeacon =
            !options.disableAutoOpen && status === Status.NOT_STARTED;

        return allSteps;
    }, [tour, options.disableAutoOpen, status]);

    const controlled = useMemo(() => {
        return tourMap[tour].find(step => step.waitForTarget);
    }, [tour]);

    const [waitFor, setWaitFor] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (waitFor) {
            const interval = setInterval(() => {
                const element = document.querySelector(`.${waitFor}`);
                if (element) {
                    setRun(true);
                    setWaitFor(undefined);
                    setIndex(prev => prev + 1);
                    clearInterval(interval);
                }
            }, 500);
            return () => {
                clearInterval(interval);
            };
        }
        return () => {};
    }, [waitFor]);

    const onboardingProps = useMemo(() => {
        return {
            stepIndex: controlled ? index : undefined,
            run: run && status !== Status.COMPLETED,
            steps,
            callback: (data: any) => {
                // handle controlled steps
                if (data.controlled) {
                    if (data.status === STATUS.FINISHED) {
                        setRun(false);
                    } else if (data.status === STATUS.SKIPPED) {
                        helpersRef.current?.close();
                    } else if (data.type === EVENTS.STEP_AFTER) {
                        if (tourMap[tour].length === data.index + 1) {
                            setRun(false);
                            setStatus(Status.COMPLETED);
                        } else if (
                            tourMap[tour][data.index + 1]?.waitForTarget
                        ) {
                            setWaitFor(tourMap[tour][data.index + 1].className);
                            setRun(false);
                        } else {
                            setIndex(data.index + 1);
                        }
                    }
                }

                // Handle local storage
                if (data.action === ACTIONS.RESET) {
                    setStatus(Status.COMPLETED);
                } else if (data.lifecycle === STATUS.READY) {
                    setStatus(Status.IN_PROGRESS);
                }
            },
            getHelpers: (helpers: StoreHelpers) => {
                helpersRef.current = helpers;
            },
        };
    }, [steps, controlled, index, run]);

    const next = useCallback(() => {
        helpersRef.current?.next();
    }, []);

    const complete = useCallback(() => {
        setStatus(Status.COMPLETED);
        helpersRef.current?.close();
    }, []);

    return {
        onboardingProps,
        next,
        complete,
    };
};
